import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EMPTY_ORG, ORG_TYPES, validateOrg } from '../../lib/organizations.js';
import { findSimilarOrganizations } from '../../lib/findSimilar.js';
import { useSimilarCheck } from '../../hooks/useSimilarCheck.js';
import SimilarEntriesWarning from '../common/SimilarEntriesWarning.jsx';
import AnnounceCheckbox from '../common/AnnounceCheckbox.jsx';

export default function OrgForm({
  initialValue,
  submitLabel,
  onSubmit,
  onCancel,
  showAnnounce = false,
  busy = false,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => ({ ...EMPTY_ORG, ...initialValue }));
  const [error, setError] = useState(null);

  // Reset only when the edited entry actually changes (different id), not
  // on every new prop reference — see comment in ScholarForm.jsx.
  useEffect(() => {
    if (initialValue) setForm({ ...EMPTY_ORG, ...initialValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue?.id]);

  function update(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
    setError(null);
  }

  const { candidates: similarCandidates } = useSimilarCheck(
    { name: form.name, website: form.website, excludeId: initialValue?.id },
    findSimilarOrganizations,
  );

  async function handleSubmit(event) {
    event.preventDefault();
    const errKey = validateOrg(form);
    if (errKey) {
      setError(t(errKey));
      return;
    }
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err.message ?? String(err));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <Field label={t('organizations.form.type')} required>
        <div className="flex flex-wrap gap-2">
          {ORG_TYPES.map((type) => (
            <label
              key={type}
              className={`cursor-pointer rounded-full border px-4 py-1.5 text-sm transition ${
                form.type === type
                  ? 'border-gold/60 bg-navy-mid/60 text-gold-light'
                  : 'border-white/10 bg-navy-deep/40 text-ink-secondary hover:border-gold/30'
              }`}
            >
              <input
                type="radio"
                name="org-type"
                value={type}
                checked={form.type === type}
                onChange={(e) => update({ type: e.target.value })}
                className="sr-only"
              />
              {t(`organizations.types.${type}`)}
            </label>
          ))}
        </div>
      </Field>

      <Field label={t('organizations.form.name')} required>
        <Input value={form.name} onChange={(v) => update({ name: v })} />
      </Field>

      <Field label={t('organizations.form.description')}>
        <Textarea rows={5} value={form.description} onChange={(v) => update({ description: v })} />
      </Field>

      <Field label={t('organizations.form.website')}>
        <Input
          type="text"
          inputMode="url"
          value={form.website}
          onChange={(v) => update({ website: v })}
          placeholder="https://…"
        />
      </Field>

      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('organizations.form.city')}>
          <Input value={form.city} onChange={(v) => update({ city: v })} />
        </Field>
        <Field label={t('organizations.form.country')}>
          <Input value={form.country} onChange={(v) => update({ country: v })} />
        </Field>
      </div>

      {form.type === 'journal' && (
        <div className="rounded-lg border border-white/10 bg-navy-base/30 p-4">
          <h3 className="mb-3 text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-gold">
            {t('organizations.form.journalSection')}
          </h3>
          <div className="grid gap-6 md:grid-cols-2">
            <Field label={t('organizations.form.publisher')}>
              <Input value={form.publisher} onChange={(v) => update({ publisher: v })} />
            </Field>
            <Field label={t('organizations.form.issn')}>
              <Input value={form.issn} onChange={(v) => update({ issn: v })} placeholder="0000-0000" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label={t('organizations.form.frequency')}>
              <Input value={form.frequency} onChange={(v) => update({ frequency: v })} placeholder="Quarterly" />
            </Field>
          </div>
          <label className="mt-4 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={Boolean(form.open_access)}
              onChange={(e) => update({ open_access: e.target.checked })}
              className="mt-1 h-4 w-4 accent-gold"
            />
            <span>
              <span className="block text-sm font-medium text-cream">{t('organizations.form.openAccess')}</span>
              <span className="mt-0.5 block text-xs text-ink-secondary">{t('organizations.form.openAccessHint')}</span>
            </span>
          </label>
        </div>
      )}

      {showAnnounce && (
        <AnnounceCheckbox
          checked={form.announce_in_whats_new}
          onChange={(v) => update({ announce_in_whats_new: v })}
        />
      )}

      <SimilarEntriesWarning candidates={similarCandidates} kind="organizations" />

      {error && (
        <p className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">{error}</p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? t('common.loading') : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
        {label}
        {required && <span className="ml-1 text-gold">*</span>}
      </span>
      {children}
    </label>
  );
}

function Input({ value, onChange, ...rest }) {
  return (
    <input
      {...rest}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="form-input"
    />
  );
}

function Textarea({ value, onChange, rows = 4, ...rest }) {
  return (
    <textarea
      {...rest}
      rows={rows}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="form-input"
    />
  );
}
